import { useState } from "react";

import {
  useAtualizarBanco,
  useBancos,
  useCriarBanco,
} from "../../lib/cadastros/hooks";
import type { BancoOut } from "../../lib/cadastros/tipos";
import { useTemPermissaoIrrestrita } from "../../lib/auth/permissoes";
import { ErroHttp } from "../../lib/http/erros";
import {
  FormularioBanco,
  type DadosBancoForm,
} from "../cadastros/FormularioBanco";

/*
 * Página de Bancos do módulo de Cadastros (FE-4 CP-C).
 *
 * Bancos são um catálogo GLOBAL (ação `cadastrar_infraestrutura`):
 *  - LEITURA é permitida a QUALQUER escopo da ação (a aba/tabela aparecem com
 *    `useTemPermissao` no layout).
 *  - ESCRITA (criar/editar) exige escopo IRRESTRITO — o backend devolve 403 a
 *    usuário escopado. Por isso os botões "Novo banco"/"Editar" são gated por
 *    `useTemPermissaoIrrestrita("cadastrar_infraestrutura")` (não só `temPermissao`),
 *    e o 403 do servidor é tratado no formulário como defesa-em-profundidade.
 *
 * Campos: criar = `codigo_compe` + `nome`; editar = só `nome` (codigo_compe
 * read-only). SEM delete.
 *
 * Estado de TELA (`useState`, não Zustand): qual formulário está aberto. As
 * mutações já invalidam o catálogo no `onSuccess` do hook — em sucesso fechamos o
 * formulário e a tabela revalida sozinha.
 *
 * SÓ classes Tailwind dos tokens CPMH.
 */

const ACAO_CADASTRAR_INFRAESTRUTURA = "cadastrar_infraestrutura";

/** Qual formulário está aberto na tela. `null` = nenhum. */
type FormAberto =
  | { tipo: "criar" }
  | { tipo: "editar"; banco: BancoOut }
  | null;

export function PaginaBancos(): JSX.Element {
  const consulta = useBancos();
  const criar = useCriarBanco();
  const atualizar = useAtualizarBanco();
  // Escrita no catálogo global exige escopo IRRESTRITO (o backend devolve 403 a
  // usuário escopado).
  const podeEscrever = useTemPermissaoIrrestrita(ACAO_CADASTRAR_INFRAESTRUTURA);

  const [form, setForm] = useState<FormAberto>(null);

  function salvarCriacao(dados: DadosBancoForm): void {
    criar.mutate(
      {
        codigo_compe: dados.codigo_compe,
        nome: dados.nome,
      },
      { onSuccess: () => setForm(null) },
    );
  }

  function salvarEdicao(banco: BancoOut, dados: DadosBancoForm): void {
    atualizar.mutate(
      {
        id: banco.id,
        // `codigo_compe` NÃO entra no PATCH (chave do catálogo, não editável).
        corpo: { nome: dados.nome },
      },
      { onSuccess: () => setForm(null) },
    );
  }

  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">Carregando bancos…</p>
    );
  }

  if (consulta.isError) {
    const mensagem =
      consulta.error instanceof ErroHttp
        ? consulta.error.message
        : "Não foi possível carregar os bancos.";
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagem}
      </p>
    );
  }

  const bancos = consulta.data;

  return (
    <div className="flex flex-col gap-4">
      {podeEscrever && (
        <div>
          <button
            type="button"
            onClick={() => setForm({ tipo: "criar" })}
            className="rounded-md border border-flame bg-flame px-4 py-1 font-subtitulo text-snow"
          >
            Novo banco
          </button>
        </div>
      )}

      {bancos.length === 0 ? (
        <p className="font-corpo text-base text-off-black">
          Nenhum banco cadastrado.
        </p>
      ) : (
        <table className="w-full border-collapse font-corpo text-base text-off-black">
          <thead>
            <tr className="border-b border-platinum text-left">
              <th className="py-2 pr-4 font-subtitulo">Código COMPE</th>
              <th className="py-2 pr-4 font-subtitulo">Nome</th>
              <th className="py-2 pr-4 font-subtitulo">Ações</th>
            </tr>
          </thead>
          <tbody>
            {bancos.map((banco) => (
              <tr key={banco.id} className="border-b border-platinum">
                <td className="py-2 pr-4">{banco.codigo_compe}</td>
                <td className="py-2 pr-4">{banco.nome}</td>
                <td className="py-2 pr-4">
                  {podeEscrever && (
                    <button
                      type="button"
                      onClick={() => setForm({ tipo: "editar", banco })}
                      className="text-flame hover:underline"
                    >
                      Editar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {form?.tipo === "criar" && (
        <FormularioBanco
          aoSalvar={salvarCriacao}
          aoCancelar={() => setForm(null)}
          salvando={criar.isPending}
          erro={criar.error}
        />
      )}

      {form?.tipo === "editar" && (
        <FormularioBanco
          banco={form.banco}
          aoSalvar={(dados) => salvarEdicao(form.banco, dados)}
          aoCancelar={() => setForm(null)}
          salvando={atualizar.isPending}
          erro={atualizar.error}
        />
      )}
    </div>
  );
}
